/**
 * ReleaseOpsTab — iter84.56.7
 *
 * Distributed-runbook layout: instead of a centralized RunbookGatePanel
 * holding every gate disposition, gate buttons (Approve / Waive / Deny)
 * live on the individual functional cards they correspond to.
 *
 * Layout:
 *   Phase 0 — Initiation              (Start promotion / active header + Cancel)
 *   Phase 1 — Preview pre-commit safety   (Parity Sweep · Deploy Readiness ·
 *                                            Values Engine)
 *   Phase 2 — CI & Deploy               (CI green on SHA ·
 *                                            ARM PROMOTE + Emergent deployment ID)
 *   Phase 3 — Verify on production         (Prod readiness · Smoke prod · UAT
 *                                            runbook · Stakeholders + FINALIZE)
 *   Phase 4 — Audit                        (Test pollution · Pre-flight ledger ·
 *                                            Orphan picks · Drift watch · Acks ·
 *                                            Archived promotions)
 *
 * iter84.57.5 — Phase 1 used to bundle CI/PR-review/Readiness/Values together,
 * which let an operator approve all gates then Save-to-GitHub with a still-
 * warning Values Engine. The fix: Phase 1 now contains ONLY the three pure
 * source-code-safety checks (Parity, Readiness, Values) and is followed by a
 * CommitGateCard that visibly locks until all three pass/waive. CI + PR review
 * + ArmPromote moved DOWN into Phase 2 (the build/arm/deploy lifecycle).
 *
 * Production (read-only): iter84.71 — hides Phases 0–4 entirely and
 * renders a single Phase 5 ("Post-deployment check"): Production View
 * read-only card + Production health banner + Post-Deploy Auto-Verify.
 */
import { useCallback, useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    AudioWaveform,
    Info,
    BookOpen,
    AlertTriangle,
    Copy,
    Check,
    Loader2,
} from "lucide-react";
import { CollapsibleCard } from "@/components/site-admin/CollapsibleCard";
import {
    RunbookInitiationCard,
    ArmPromoteCard,
    FinalizeRunbookCard,
} from "@/components/site-admin/RunbookPhaseCards";
import { CommitGateCard } from "@/components/site-admin/CommitGateCard";
// iter84.59.18 — Foundational helpers extracted from ReleaseOpsTab so
// future per-phase JSX splits (iter84.59.19+) can import them without
// circular-import gymnastics.
import {
    CollapsiblePhaseSection,
} from "@/components/site-admin/release-ops/CollapsiblePhaseSection";
// iter84.59.19 — Phase 2 and Phase 4 BODY content extracted to per-phase
// component files in `release-ops/`. iter84.59.21 — Phase 1 and Phase 3
// followed. The CollapsiblePhaseSection wrappers stay in the orchestrator
// so call-site test assertions continue to pass.
import { Phase1PreCommitSafetyBody } from "@/components/site-admin/release-ops/Phase1PreCommitSafetyBody";
import { Phase2CIAndDeployBody } from "@/components/site-admin/release-ops/Phase2CIAndDeployBody";
import { Phase3VerifyOnProductionBody } from "@/components/site-admin/release-ops/Phase3VerifyOnProductionBody";
import { Phase4AuditSentinelBody } from "@/components/site-admin/release-ops/Phase4AuditSentinelBody";
// iter84.71 — Production-only Phase 5 (post-deployment check).
import { Phase5PostDeploymentBody } from "@/components/site-admin/release-ops/Phase5PostDeploymentBody";

export default function ReleaseOpsTab({ onOpenGameOps } = {}) {
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [prodProbe, setProdProbe] = useState(null);
    const [staleArm, setStaleArm] = useState(null);
    // iter84.56.7-h — Verdicts emitted by child cards so the parent
    // can outline the matching CollapsibleCard with the right color.
    const [ciVerdict, setCiVerdict] = useState(null);
    // iter84.56.7-h — Active promotion lifted to top of the tab so each
    // phase card can read + mutate it directly. Replaces the
    // centralized RunbookGatePanel ownership.
    const [activePromotion, setActivePromotion] = useState(null);
    const [archivedRows, setArchivedRows] = useState([]);
    const [currentIter, setCurrentIter] = useState("");
    const isArmed = !!(
        activePromotion &&
        activePromotion.status === "in_progress" &&
        activePromotion.armed_at
    );

    const reloadActivePromotion = useCallback(async (uiMode) => {
        try {
            if (uiMode === "archive") {
                const { rows } = await api.runbookArchiveList();
                setArchivedRows(rows || []);
                setActivePromotion(null);
                return;
            }
            const rows = await api.runbookListPromotions();
            const inProgress = rows.find((p) => p.status === "in_progress");
            if (inProgress) {
                const full = await api.runbookGetPromotion(inProgress.id);
                setActivePromotion(full);
            } else {
                setActivePromotion(null);
            }
        } catch {
            /* silent — release ops shouldn't error-toast on background load */
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        api.releaseOpsOverview()
            .then((r) => {
                if (cancelled) return;
                setOverview(r);
                const key = `release-ops-onboard-dismissed-${r?.environment || "preview"}`;
                setBannerDismissed(localStorage.getItem(key) === "1");
                reloadActivePromotion(r?.ui_mode || "operational");
            })
            .catch((err) => {
                const msg = err.response?.data?.detail || "Failed to load Release Ops overview";
                if (!cancelled) toast.error(msg);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        api.releaseOpsProdVersionProbe().then((r) => { if (!cancelled && r) setProdProbe(r); }).catch(() => {});
        api.releaseOpsStaleArmWarning().then((r) => { if (!cancelled && r) setStaleArm(r); }).catch(() => {});
        api.changelogTop().then((r) => { if (!cancelled && r?.iter) setCurrentIter(r.iter); }).catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [reloadActivePromotion]);

    // iter84.56 — Poll for SHA drift / state changes every 30s while a
    // promotion is in-progress.
    useEffect(() => {
        if (!activePromotion || activePromotion.status !== "in_progress") return undefined;
        let cancelled = false;
        const id = setInterval(async () => {
            try {
                const full = await api.runbookGetPromotion(activePromotion.id);
                if (!cancelled) setActivePromotion(full);
            } catch { /* silent */ }
        }, 30000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePromotion?.id, activePromotion?.status]);

    const dismissBanner = () => {
        const key = `release-ops-onboard-dismissed-${overview?.environment || "preview"}`;
        localStorage.setItem(key, "1");
        setBannerDismissed(true);
    };

    // iter84.56.7-e/f — Shared auto-flip helper. Cards that emit a
    // verdict (clear / warn / fail) can hand it back to auto-resolve
    // their gate without operator clicks. Safe by default: only flips
    // gates currently in `pending` state, never overwriting operator
    // decisions. `fail` always leaves the gate Pending (human must
    // decide what to do with a failing signal).
    // Hook declared BEFORE the early `if (loading) return` to honor
    // React's rules-of-hooks (must be called in the same order each
    // render).
    const autoFlipGate = useCallback(
        async ({ section, itemId, verdict, summary }) => {
            const pid = activePromotion?.id;
            if (!pid) return;
            const current = activePromotion?.checks?.[`${section}:${itemId}`];
            if (current?.state && current.state !== "pending") return;
            try {
                if (verdict === "clear" || verdict === "pass" || verdict === "ok") {
                    const r = await api.runbookUpdateCheck(pid, {
                        item_id: itemId,
                        section,
                        state: "complete",
                    });
                    setActivePromotion(r);
                } else if (verdict === "warn") {
                    const w = summary?.warn_count ?? summary?.warned ?? 0;
                    const a = summary?.acked || 0;
                    const reason = `Auto-waived: ${w} warn${a ? `, ${a} ack'd` : ""}`;
                    const r = await api.runbookUpdateCheck(pid, {
                        item_id: itemId,
                        section,
                        state: "waived",
                        waiver_reason: reason,
                    });
                    setActivePromotion(r);
                }
                // verdict === "fail" → leave pending; operator must decide.
            } catch {
                // Silent — operator can still click manually.
            }
        },
        [activePromotion],
    );

    // iter84.57.26 — Computed BEFORE the loading early-return so the
    // `useMemo` below honours React's rules-of-hooks.
    const environment = overview?.environment || "preview";
    const uiMode = overview?.ui_mode || "operational";
    const isProd = environment === "production";

    // Active phase = first phase with at least one non-complete gate.
    // Phase 0 (initiation) and Phase 4 (audit) are non-gated and don't
    // participate in the active-phase rotation.
    const activePhaseNumber = useMemo(() => {
        const isPhaseActive = (gates) =>
            gates.some(([s, i]) => {
                const c = activePromotion?.checks?.[`${s}:${i}`];
                return (c?.state || "pending") !== "complete";
            });
        const phase1 = [
            ["pre_deploy", "parity_sweep_clear"],
            ["pre_deploy", "readiness_green"],
            ["pre_deploy", "values_check_preview"],
            ["pre_deploy", "vendor_health_green"],
            ["pre_deploy", "auto_verify_preview"],
            ["pre_deploy", "preview_acceptance"], // iter86.19.1 — keeps Phase 1 active until dispositioned.
        ];
        const phase2 = [
            ["pre_deploy", "ci_green"],
        ];
        const phase3 = isProd
            ? [
                ["post_deploy", "prod_readiness_green"],
                ["post_deploy", "auto_verify_prod"],
            ]
            : [
                ["post_deploy", "prod_readiness_green"],
                ["post_deploy", "uat_runbook_complete"],
                ["post_deploy", "auto_verify_prod"],
                // iter86.35 — review travels with the dev → main PROMOTE PR.
                ["post_deploy", "pr_approved"],
            ];
        if (!isProd && isPhaseActive(phase1)) return 1;
        if (!isProd && isPhaseActive(phase2)) return 2;
        if (isPhaseActive(phase3)) return 3;
        return null; // All gates complete — no active phase.
    }, [activePromotion, isProd]);

    if (loading) {
        return (
            <div className="brutal-card p-6 text-center" data-testid="release-ops-loading">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
                <p className="text-xs text-zinc-500 mt-2">Loading Release Ops…</p>
            </div>
        );
    }

    // iter84.57.26 — Per-phase gate id catalogues: each section reads its
    // own status (warn / blocker / pending / complete) for its inline pill.
    const PHASE_1_GATES = [
        ["pre_deploy", "parity_sweep_clear"],
        ["pre_deploy", "readiness_green"],
        ["pre_deploy", "values_check_preview"],
        ["pre_deploy", "vendor_health_green"],
        ["pre_deploy", "auto_verify_preview"],
        ["pre_deploy", "preview_acceptance"],
    ];
    const PHASE_2_GATES = [
        ["pre_deploy", "ci_green"],
    ];
    const PHASE_3_GATES = isProd
        ? [
            ["post_deploy", "prod_readiness_green"],
            ["post_deploy", "auto_verify_prod"],
        ]
        : [
            ["post_deploy", "prod_readiness_green"],
            ["post_deploy", "uat_runbook_complete"],
            ["post_deploy", "auto_verify_prod"],
            ["post_deploy", "vendor_health_prod"],
            ["post_deploy", "game_health_prod"],
        ]; // iter86.82 — pr_approved retired → batched Sync to Main (Phase 4).

    const MODE_PILL = isProd
        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
        : "bg-sky-500/15 text-sky-300 border-sky-500/40";
    const modeLabel = isProd ? "Production · Audit & Health" : "Preview · Operational";
    const heroCopy = isProd
        ? "You're viewing the LIVE production console. This tab is read-only here: Phase 5 below confirms the deploy landed cleanly (Production health + Auto-Verify). To prepare a new release, do it on preview."
        : "You're viewing the PREVIEW console. Walk the phases below — each functional card carries its own Approve / Waive / Deny gate. Phase 1 is pre-commit safety; once the Commit gate turns green you Save-to-GitHub (branch: dev), then Phase 2 confirms CI on dev + ARM PROMOTE; Phase 3 verifies production and you finalize — the cycle closes there. The dev → main merge is the batched Sync to Main ritual (Phase 4 card), on its own cadence.";

    const promotionId = activePromotion?.id || null;
    const promotionStatus = activePromotion?.status;

    // Common props sent to every GateButtons instance.
    const gateCtx = {
        promotionId,
        promotionStatus,
        onUpdated: (refreshed) => setActivePromotion(refreshed),
    };

    return (
        <div className="space-y-4" data-testid="release-ops-tab" data-environment={environment}>
            {/* Hero card */}
            <div className="brutal-card p-4 border-2 border-volt/40 bg-volt/[0.03]">
                <div className="flex items-start gap-3">
                    <AudioWaveform className="w-6 h-6 text-volt shrink-0" />
                    <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h2 className="font-display font-extrabold text-base uppercase tracking-wide">
                                Rhythm
                            </h2>
                            <span
                                className={`pill text-[10px] font-bold uppercase border ${MODE_PILL}`}
                                data-testid="release-ops-mode-pill"
                            >
                                {modeLabel}
                            </span>
                            {prodProbe?.enabled && (
                                <span
                                    data-testid="release-ops-drift-chip"
                                    className={`pill text-[10px] font-bold uppercase border ${
                                        prodProbe.drift === "in_sync" ? "bg-cyber/10 border-cyber/40 text-cyber" :
                                        prodProbe.drift === "preview_ahead" ? "bg-blaze/10 border-blaze/40 text-blaze" :
                                        prodProbe.drift === "prod_ahead" ? "bg-heat/10 border-heat/40 text-heat" :
                                        "bg-zinc-700/20 border-zinc-600 text-zinc-400"
                                    }`}
                                    title={prodProbe.drift_label}
                                >
                                    {prodProbe.drift === "in_sync" ? "prod in sync" :
                                     prodProbe.drift === "preview_ahead" ? `prod behind · ${prodProbe.prod_iter || "?"}` :
                                     prodProbe.drift === "prod_ahead" ? "prod ahead (check probe)" :
                                     "prod unreachable"}
                                </span>
                            )}
                            {staleArm?.warning && (
                                <span
                                    data-testid="release-ops-stale-arm-chip"
                                    className="pill text-[10px] font-bold uppercase border bg-heat/15 border-heat text-heat animate-pulse"
                                    title={staleArm.message}
                                >
                                    ⚠ armed {staleArm.armed_hours_ago}h ago · not finalized
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-zinc-300 leading-relaxed">
                            {heroCopy}
                        </p>
                        <p className="text-[10px] text-zinc-500 mt-2">
                            Signed in as <span className="font-bold text-zinc-300">{overview?.viewer?.email}</span> · role <span className="font-bold text-volt">{overview?.viewer?.role}</span>
                        </p>
                    </div>
                </div>
            </div>

            {!bannerDismissed && (
                <div
                    className={`brutal-card p-3 border-2 ${isProd ? "border-emerald-500/40 bg-emerald-500/[0.03]" : "border-sky-500/40 bg-sky-500/[0.03]"} flex items-start gap-3`}
                    data-testid="release-ops-onboarding-callout"
                >
                    <Info className={`w-4 h-4 shrink-0 mt-0.5 ${isProd ? "text-emerald-400" : "text-sky-400"}`} />
                    <div className="flex-1 text-xs leading-relaxed text-zinc-300">
                        <p className="font-bold text-white mb-1">
                            {isProd ? "How to use this tab on production" : "How the phased runbook works"}
                        </p>
                        {isProd ? (
                            <ul className="space-y-1 list-disc list-inside text-zinc-300">
                                <li>Everything here is read-only — the runbook phases live on preview.</li>
                                <li><span className="text-emerald-300 font-bold">Phase 5 — Post-deployment check</span>: confirm <span className="font-bold">Production health</span> is GREEN, then run <span className="font-bold">Auto-Verify</span>.</li>
                                <li>To prepare a new release, switch to preview.</li>
                            </ul>
                        ) : (
                            <ul className="space-y-1 list-disc list-inside text-zinc-300">
                                <li><span className="text-sky-300 font-bold">Phase 0</span> — Start a promotion (gives every gate a record).</li>
                                <li><span className="text-sky-300 font-bold">Phase 1</span> — Pre-commit safety. The numbered gate cards in the section are the authoritative list (defined by the promotion template). Approve / Waive / Deny each one inline.</li>
                                <li><span className="text-sky-300 font-bold">Commit gate</span> — Unlocks Save-to-GitHub (target branch <span className="font-mono">dev</span> — main only changes via the PROMOTE merge). Stays locked until every Phase-1 gate (incl. Preview acceptance) is pass/waived.</li>
                                <li><span className="text-sky-300 font-bold">Phase 2</span> — CI green on the shipped dev SHA + ARM PROMOTE → deploy in Emergent → paste deployment ID.</li>
                                <li><span className="text-sky-300 font-bold">Phase 3</span> — Approve / Waive / Deny each post-deploy gate against production (incl. manual Auto-Verify approval), then FINALIZE (type PROMOTE). The cycle closes here — start a fresh promotion anytime.</li>
                                <li><span className="text-sky-300 font-bold">Phase 4</span> — Audit & Sentinel: 4.1 Sync to Main (the batched <span className="font-mono">dev → main</span> PR, own cadence) + pre-flight ledger + promotion archive · 4.2 canon decisions (acknowledgements, stewardship).</li>
                            </ul>
                        )}
                    </div>
                    <button
                        onClick={dismissBanner}
                        data-testid="release-ops-onboarding-dismiss-btn"
                        className="brutal-btn-ghost text-[10px] py-1 px-2 shrink-0"
                    >
                        Got it
                    </button>
                </div>
            )}

            {/* iter84.71 — Deploy Workflow guide is a preview (runbook)
                concern; production's simplified surface doesn't show it. */}
            {!isProd && <DeployWorkflowAccordion activePromotion={activePromotion} />}

            {/* ----------------------------- PHASE 0 ----------------------------- */}
            {/* iter84.57.26 — Phase 0 stays always-open: it's the entry
                point with the promotion record. iter84.71 — preview-only
                (production renders the read-only promotion card inside
                Phase 5 instead). */}
            {!isProd && (
            <CollapsiblePhaseSection
                number="0"
                title="Initiation · start the runbook"
                hint="every gate below is recorded against this promotion"
                accent="volt"
                gateIds={[]}
                promotion={activePromotion}
                isActivePhase={false}
                forceOpen
            >
                <RunbookInitiationCard
                    promotion={activePromotion}
                    onUpdated={setActivePromotion}
                    onCancelled={() => {
                        setActivePromotion(null);
                        reloadActivePromotion(uiMode);
                    }}
                    environment={environment}
                />
            </CollapsiblePhaseSection>
            )}

            {/* ----------------------------- PHASE 1 ----------------------------- */}
            {!isProd && (
                <>
                    {/* iter84.57.5 — Phase 1 restructured to the three pure
                      * pre-commit safety checks (Parity Sweep, Deploy
                      * Readiness, Values Engine). CI / Open PRs / Code
                      * review moved DOWN into the new Phase 2 ("CI &
                      * Deploy") because they describe build-and-arm state,
                      * not source-code safety. The CommitGateCard sits
                      * between Phase 1 and Phase 2 and renders a hard
                      * lock until all three Phase-1 gates are pass/waived
                      * — the iter84.57.5 fix for "I committed too early
                      * and the warnings surfaced post-deploy."
                      */}
                    <CollapsiblePhaseSection
                        number="1"
                        title="Preview · pre-commit safety"
                        hint="every gate below must be clean (or waived) before you Save-to-GitHub"
                        accent="cyber"
                        gateIds={PHASE_1_GATES}
                        promotion={activePromotion}
                        isActivePhase={activePhaseNumber === 1}
                    >
                        <Phase1PreCommitSafetyBody
                            gateCtx={gateCtx}
                            activePromotion={activePromotion}
                            environment={environment}
                            autoFlipGate={autoFlipGate}
                            isArmed={isArmed}
                            onOpenGameOps={onOpenGameOps}
                            currentIter={currentIter}
                        />
                    </CollapsiblePhaseSection>

                    {/* iter84.57.5 — Hard commit gate. Reads the 3 Phase-1
                      * gate states and renders a locked banner until all
                      * are pass/waived. Operator cannot Save-to-GitHub
                      * (Emergent's external commit control) responsibly
                      * before this turns green.
                      *
                      * iter84.57.6 — Now also handles SHA capture: once
                      * unlocked, the operator clicks "I've Saved to
                      * GitHub" and the server fetches top-of-main and
                      * stamps it on the promotion.
                      *
                      * iter84.57.26 — Sits BETWEEN Phase 1 and Phase 2's
                      * collapsibles, always-visible, so the lock/unlock
                      * cue is never hidden by a collapsed phase.
                      */}
                    <CommitGateCard
                        promotion={activePromotion}
                        onUpdated={setActivePromotion}
                    />

                    {/* ----------------------------- PHASE 2 ----------------------------- */}
                    <CollapsiblePhaseSection
                        number="2"
                        title="CI & Deploy"
                        hint="post-commit: confirm CI is green on the shipped SHA, then ARM PROMOTE"
                        accent="fuchsia"
                        gateIds={PHASE_2_GATES}
                        promotion={activePromotion}
                        isActivePhase={activePhaseNumber === 2}
                    >
                        <Phase2CIAndDeployBody
                            gateCtx={gateCtx}
                            activePromotion={activePromotion}
                            ciVerdict={ciVerdict}
                            setCiVerdict={setCiVerdict}
                        />
                    </CollapsiblePhaseSection>

                    {/* iter84.57.5 — ArmPromote folded into Phase 2 (was
                      * its own Phase 2). The user's mental model groups
                      * all build-and-arm activity into a single "after
                      * commit, before verifying on prod" phase.
                      *
                      * iter84.58 — Mirrored the CommitGateCard placement:
                      * sits BETWEEN Phase 2 and Phase 3 collapsibles,
                      * always-visible, so the ARM cue is never hidden
                      * by a collapsed Phase 2. Matches the Phase 1
                      * COMMITTED card behaviour the operator expects.
                      */}
                    <ArmPromoteCard
                        promotion={activePromotion}
                        onUpdated={setActivePromotion}
                        environment={environment}
                    />
                </>
            )}

            {/* ----------------------------- PHASE 3 (post-deploy) ----------------------------- */}
            {/* iter84.71 — preview-only: the operator walks these gates from
                preview while watching the production app in another tab.
                Production's own surface is Phase 5 below. */}
            {!isProd && (
            <CollapsiblePhaseSection
                number="3"
                title="Verify on production"
                hint="open production in incognito, run gates here against prod"
                accent="emerald"
                gateIds={PHASE_3_GATES}
                promotion={activePromotion}
                isActivePhase={activePhaseNumber === 3}
            >
                <Phase3VerifyOnProductionBody
                    gateCtx={gateCtx}
                    activePromotion={activePromotion}
                    isProd={isProd}
                    autoFlipGate={autoFlipGate}
                />
            </CollapsiblePhaseSection>
            )}

            {/* iter84.58 — FinalizeRunbookCard sits OUTSIDE Phase 3's
              * collapsible (like the CommitGate/ARM cards) so the final
              * "FINALIZE" cue is never hidden by a collapse. Preview-only. */}
            {!isProd && (
                <FinalizeRunbookCard
                    promotion={activePromotion}
                    onUpdated={(p) => {
                        setActivePromotion(p);
                        // After finalize the active promotion flips to finalized;
                        // reload list so it moves into the archive section.
                        if (p?.status === "finalized") {
                            setTimeout(() => reloadActivePromotion(uiMode), 500);
                        }
                    }}
                    environment={environment}
                />
            )}

            {/* ----------------------------- PHASE 4 ----------------------------- */}
            {/* iter84.57.26 — Phase 4 has no gates (audit-only) so it
                starts collapsed by default. iter84.71 — preview-only:
                production's simplified surface is Phase 5 below. */}
            {!isProd && (
            <CollapsiblePhaseSection
                number="4"
                title="Audit · history & sentinels"
                hint="historical record + read-only audits"
                accent="zinc"
                gateIds={[]}
                promotion={activePromotion}
                isActivePhase={false}
            >
                <Phase4AuditSentinelBody
                    environment={environment}
                    isProd={isProd}
                    uiMode={uiMode}
                    archivedRows={archivedRows}
                    setActivePromotion={setActivePromotion}
                    reloadActivePromotion={reloadActivePromotion}
                />
            </CollapsiblePhaseSection>
            )}

            {/* ----------------------------- PHASE 5 (production only) ----------------------------- */}
            {/* iter84.71 — Production's entire operational surface:
                Post-deployment check. 5.1 Production View read-only ·
                5.2 Production health · 5.3 Post-Deploy Auto-Verify.
                Hidden on preview (preview's Auto-Verify runner lives in
                Phase 1 sub-step 1.5). Always open — it's the only
                section on the page. */}
            {isProd && (
            <CollapsiblePhaseSection
                number="5"
                title="Post-deployment check"
                hint="read-only — confirm the deploy landed cleanly"
                accent="emerald"
                gateIds={[]}
                promotion={activePromotion}
                isActivePhase={false}
                forceOpen
            >
                <Phase5PostDeploymentBody
                    activePromotion={activePromotion}
                    setActivePromotion={setActivePromotion}
                    reloadActivePromotion={reloadActivePromotion}
                    uiMode={uiMode}
                    environment={environment}
                    autoFlipGate={autoFlipGate}
                />
            </CollapsiblePhaseSection>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// CopyChip — used inside DeployWorkflowAccordion.
// ---------------------------------------------------------------------------
function CopyChip({ value, display, label, className = "", testId }) {
    const [copied, setCopied] = useState(false);
    const handle = (e) => {
        e?.preventDefault();
        if (!value) return;
        navigator.clipboard.writeText(value).then(
            () => { setCopied(true); toast.success(label ? `${label} copied` : "Copied"); setTimeout(() => setCopied(false), 1500); },
            () => toast.error("Copy failed"),
        );
    };
    return (
        <button
            type="button"
            onClick={handle}
            data-testid={testId}
            title={`Click to copy: ${value || "—"}`}
            disabled={!value}
            className={`inline-flex items-center gap-1 align-baseline font-mono text-[10px] px-1.5 py-0.5 border rounded-sm transition-colors ${
                copied
                    ? "border-emerald-400 bg-emerald-500/15 text-emerald-300"
                    : "border-cyber/40 bg-cyber/10 text-cyber hover:border-cyber hover:bg-cyber/20"
            } disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
        >
            {copied ? <Check className="w-2.5 h-2.5" /> : <Copy className="w-2.5 h-2.5" />}
            <span className="truncate max-w-[28ch]">{display ?? value ?? "—"}</span>
        </button>
    );
}

// ---------------------------------------------------------------------------
// iter84.59.19 — `RecentDeploysCardContent` and `OpenPullRequestsCardContent`
// extracted to `./release-ops/CIStatusCards.jsx`. The Phase 2 body that
// rendered them lives in `./release-ops/Phase2CIAndDeployBody.jsx`.
// Phase 4 body lives in `./release-ops/Phase4AuditSentinelBody.jsx`.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// DeployWorkflowAccordion — slightly slimmed to reflect the new phase
// numbering. References to "Runbook Gate" updated to "Phase 0 / 2 / 3".
// ---------------------------------------------------------------------------
function DeployWorkflowAccordion({ activePromotion }) {
    const [latestSha, setLatestSha] = useState("");
    const [latestShortSha, setLatestShortSha] = useState("");
    const [latestMessage, setLatestMessage] = useState("");
    const [changelogLatest, setChangelogLatest] = useState("");
    const [activeTitle, setActiveTitle] = useState("");
    const [techDebtDelta, setTechDebtDelta] = useState({ sweeps: [], summary: { sweep_count: 0, total_loc_saved: 0 } });

    // iter84.57.6 — Prefer the SHA captured on the active promotion
    // (set by the CommitGateCard via `runbookCaptureSha`). Falls through
    // to the live GitHub top-of-main SHA if no promotion is active or
    // SHA hasn't been captured yet. Single source-of-truth for the
    // Phase 2 deploy-guide chip: operator clicks ONCE in the Commit
    // gate, paste-ready value flows downstream.
    const capturedSha = activePromotion?.target_sha || "";
    const displaySha = capturedSha || latestSha;
    const displayShortSha = capturedSha
        ? String(capturedSha).slice(0, 7)
        : latestShortSha;

    useEffect(() => {
        let cancelled = false;
        api.githubRecentCommits().then((r) => {
            if (cancelled) return;
            const rows = r?.rows || [];
            const top = rows[0];
            if (top) {
                setLatestSha(top.sha || "");
                setLatestShortSha(top.short_sha || "");
            }
            const looksAuto = (s) => /^(auto[- ]?commit|auto[- ]?generated)/i.test((s || "").trim());
            const iterMatch = (s) => /^iter\s*\d/i.test((s || "").trim());
            const firstLine = (s) => (s || "").split("\n")[0].trim();
            const named = rows.find((c) => iterMatch(c.message))
                ?? rows.find((c) => !looksAuto(c.message));
            if (named) setLatestMessage(firstLine(named.message));
        }).catch(() => {});
        api.runbookListPromotions().then((rows) => {
            if (cancelled) return;
            const inProgress = (rows || []).find((p) => p.status === "in_progress");
            if (inProgress?.title) setActiveTitle(inProgress.title);
        }).catch(() => {});
        api.changelogLatest().then((r) => {
            if (cancelled) return;
            if (r?.latest) setChangelogLatest(r.latest);
        }).catch(() => {});
        api.releaseOpsTechDebtDelta().then((r) => {
            if (cancelled) return;
            if (r) setTechDebtDelta(r);
        }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const fallbackTitle = latestShortSha ? `iter##.# — what shipped (${latestShortSha})` : "iter##.# — what shipped";
    const suggestedTitle = activeTitle || changelogLatest || latestMessage || fallbackTitle;

    return (
        <CollapsibleCard
            testId="deploy-workflow-accordion"
            icon={BookOpen}
            title="Deploy Workflow — Step-by-step guide"
        >
            {/* iter84.71 — Production branch removed: the accordion only
                renders on preview now (production shows Phase 5 instead). */}
            <div className="text-xs text-zinc-300 leading-relaxed space-y-3" data-testid="deploy-workflow-body">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-sky-400">
                        Walk these every deploy.
                    </p>
                    {techDebtDelta.summary.sweep_count > 0 && (
                        <div className="brutal-card-light p-2 border-cyber/40" data-testid="deploy-guide-tech-debt-delta-card">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-cyber mb-1">Tech-debt receipt</p>
                            <p className="text-zinc-300">
                                This deploy includes{" "}
                                <span className="font-bold text-cyber">{techDebtDelta.summary.sweep_count}</span>{" "}
                                sweep{techDebtDelta.summary.sweep_count === 1 ? "" : "s"} ·{" "}
                                <span className="font-bold text-cyber">{techDebtDelta.summary.total_loc_saved}</span>{" "}
                                lines saved.
                            </p>
                        </div>
                    )}
                    <div>
                        <p className="font-bold text-white mb-1">Phase 0 — Initiation</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                            <li className="flex items-baseline gap-2 flex-wrap">
                                <span>Suggested title:</span>
                                <CopyChip testId="deploy-guide-suggested-title-chip" value={suggestedTitle} label="Suggested title" />
                            </li>
                            <li>Click <span className="font-bold text-white">Start promotion</span> in the Phase 0 card.</li>
                        </ol>
                    </div>
                    <div>
                        <p className="font-bold text-white mb-1">Phase 1 — Pre-commit safety</p>
                        {/* iter86.19.2 — Generalized (operator request): the guide
                          * describes the PATTERN, the phase section above renders
                          * the actual gate list from the promotion template — so
                          * this copy can never drift when gates are added/renamed. */}
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                            <li>Open the Phase 1 section and work through <span className="font-bold text-white">every gate card top-to-bottom</span> — run its check, then Approve / Waive (with reason) / Deny inline. The section pill turns COMPLETE when all are dispositioned.</li>
                            <li>When the <span className="font-bold text-emerald-400">Commit gate</span> turns green, Save-to-GitHub via the chat input — <span className="font-bold text-white">select branch <span className="font-mono">dev</span></span> (never main; main only changes via the PROMOTE merge).</li>
                            <li className="flex items-baseline gap-2 flex-wrap">
                                <span>Click <span className="font-bold text-emerald-400">I've Saved to GitHub — Capture SHA</span> in the Commit gate:</span>
                                <CopyChip testId="deploy-guide-latest-sha-chip" value={displaySha} display={displayShortSha || "—"} label="SHA" />
                            </li>
                        </ol>
                    </div>
                    <div>
                        <p className="font-bold text-white mb-1">Phase 2 — CI & Deploy</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                            <li>Wait for GitHub Actions to go GREEN on the shipped dev SHA, then disposition the Phase 2 gates.</li>
                            <li>Click <span className="font-bold text-white">ARM PROMOTE</span> → Manage Deployments → Deploy → paste the deployment ID back.</li>
                        </ol>
                    </div>
                    <div>
                        <p className="font-bold text-white mb-1">Phase 3 — Verify + Finalize</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                            <li>Open <a href="https://tinycrewrivalries.com/platform" target="_blank" rel="noopener noreferrer" className="text-volt underline">tinycrewrivalries.com/platform</a> in another tab; disposition each post-deploy gate against production (3.1 readiness · 3.2 Auto-Verify).</li>
                            <li>Click <span className="font-bold text-white">FINALIZE — Type PROMOTE</span>. The cycle is closed — start a fresh promotion whenever the next iter lands.</li>
                        </ol>
                    </div>
                    <div>
                        <p className="font-bold text-sky-400 mb-1">Sync to Main — dev → main (batched · own cadence)</p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                            <li><span className="font-bold text-white">NOT per iter</span> — ~daily, batch every iter since the last sync into ONE PR: GitHub → New PR → base <span className="font-mono">main</span> ← compare <span className="font-mono">dev</span>.</li>
                            <li>CI re-runs on this PR (Backend Tests + Frontend Build). Wait for green, review the batched diff, Merge. The <span className="font-bold text-white">Sync to Main card</span> in Phase 4 tracks how far dev is ahead.</li>
                        </ol>
                    </div>
                    <div className="border-t border-zinc-800 pt-3">
                        <p className="font-bold text-amber-400 mb-1 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" /> If a gate goes red mid-flow
                        </p>
                        <p className="text-[11px] text-zinc-400 leading-relaxed">
                            Use the <span className="font-bold">Deny</span> button (with a reason) to record the failure
                            without abandoning the promotion. Fix the issue, redeploy, then change the gate to
                            Approve or Waive. If the fix required a <span className="font-bold">new commit</span> (e.g. CI failed), Save to GitHub again, then hit <span className="font-bold">Re-capture SHA</span> on the Phase 1 Commit gate so the promotion stamps the new head — no need to cancel. If the whole flow needs scrapping, click <span className="font-bold">Cancel promotion</span>
                            {" "}in the Phase 0 header and start fresh.
                        </p>
                    </div>
                </div>
        </CollapsibleCard>
    );
}

// ---------------------------------------------------------------------------
// iter84.59.18 — `PromotionArchiveCard`, `DriftWatchCardContent`,
// `CollapsiblePhaseSection` + `checkOf` extracted to
// `./release-ops/*.jsx`. See top-of-file imports.
// ---------------------------------------------------------------------------
